"use client";
// React imports
import React, { useState, Suspense, lazy, useEffect, useRef } from "react";

// Next imports
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";

// External library imports
import { Box } from "@mui/material";

// Internal library imports
import { Card, showToast } from "@int-traderjoes/ui-components-library";

// Context imports
import { useTheme } from '@/context';
import { useAuth } from "@/context/AuthContext";
import { useGlobalStateContext } from "@/context/GlobalContext";
import { TaskList, Task, GroupedTasks } from "@/context/data.types";
import { useLanguageContext } from "@/context/LanguageContext";

// services imports
import { productService, vendorGuestService } from "@/services";

// Utils imports
import Features from "@/utils/json/featuresLoader";
import { fetchTaskList, getAggregatedTaskList } from "@/utils/taskhelper";
import { fetchRoleBasedAccess } from "@/utils/roleBasedAccessHelper";

// Styles imports
import './dashboard.scss';
import useTask from "@/hooks/useTask";
import { useReadyForOrdersLabel, READY_FOR_ORDERS_FEATURE_KEY } from "@/hooks/useReadyForOrdersLabel";
import { useVendorManagementLabel, VENDOR_MANAGEMENT_FEATURE_KEY } from "@/hooks/useVendorManagementLabel";

// Lazy load the components
const DashboardSubmenu = lazy(() => import("./DashboardSubmenu"));
const DashboardTileContent = lazy(() => import("./DashboardTileContent"));

// DashboardIcon Component
const DashboardIcon: React.FC<{ src: string; alt: string }> = ({
  src,
  alt,
}) => (
  <div className="relative w-[80px] h-[80px]">
    <Image
      src={src}
      alt={alt}
      fill
    />
  </div>
);

const isVendor = (userDetails: any) => userDetails?.roles?.[0]?.rolename?.split("|")[0] === "VENDOR";

const DashboardCard: React.FC = () => {
  const router = useRouter();
  const [cardExpanded, setCardExpanded] = useState<string | number | null>(
    null
  );
  const [allowedFeatures, setAllowedFeatures] = useState<Array<string>>([]);
  const [allowedEpics, setAllowedEpics] = useState<Array<string>>([]);
  const [taskList, setTaskList] = useState<TaskList>([]);
  const errorToastShown = useRef(false); // Ref to track if the toast has been shown
  const { refreshGetMyTask } = useTask();
  const readyForOrdersLabel = useReadyForOrdersLabel();
  const vendorManagementLabel = useVendorManagementLabel();


  const { setIsLoading, taskCount, setTaskCount, setRoleBasedAccessPermissions, roleBasedAccessPermissions,} = useGlobalStateContext();
  const { darkMode } = useTheme();
  const { userDetails } = useAuth();
  const { languageText } = useLanguageContext();
  const buttonText = languageText?.BUTTONS;
  const toastText = languageText?.MESSAGES?.TOAST_MESSAGES?.MY_TASKS;


  let { mainSidebarItems } = Features;
  if (userDetails?.roles?.[0]?.rolename?.split("|")[0] === 'VENDOR') {
    mainSidebarItems = mainSidebarItems.filter((item) => (item.key === 'myTasks'));
  }

  useEffect(() => {
   const loadTask = async () => {
     try {
       // Fetch group counts and names for dashboard (groupCount/generic called here when vendor lands on home)
       const data = await refreshGetMyTask({requiredLoader: false, fetchTasks: false});
       const dataTaskList = data?.taskList ?? [];
       const dataTaskCount = data?.taskCount ?? 0;
       setTaskList(dataTaskList);
       setTaskCount(dataTaskCount);
       // Vendor with no tasks: redirect only when we got a successful response (not on API failure)
       const hasValidResponse = data != null && Array.isArray(dataTaskList);
       if (isVendor(userDetails) && hasValidResponse && dataTaskCount === 0) {
         router.replace("/task-not-found");
       }
     } catch (err) {
       console.error(err);
     }
   };

    if (userDetails) {
      loadTask();
    }
    // userDetails?.roles?.forEach((user: any) => {
    //   if(user.rolename.split("|")[0] !== undefined){
    //     fetchTaskListPromise(user.rolename.split("|")[0], false);
        
    //   }
    // });
    
    //fetch permissions for features
    const roleBasedAccessPermissionUnion = fetchRoleBasedAccess(userDetails?.roles || [])
    console.log("roleBasedAccessPermissionUnion", roleBasedAccessPermissionUnion);
    
    setRoleBasedAccessPermissions(roleBasedAccessPermissionUnion);
    const allowedFeatureIds = Object.keys(roleBasedAccessPermissionUnion)
    setAllowedFeatures(allowedFeatureIds);
  }, [userDetails, router]);

  useEffect(() => {
    const staticEpics = ["myTasks"];
    // if (taskList?.length) {
    //   staticEpics.push("myTasks");
    // }

    const filteredEpics = Features.mainSidebarItems.filter((epic)=>{
      return epic.submenu?.some((submenuItem)=>{
        return allowedFeatures.includes(submenuItem.id.toString())
      })
    }).map(epic=>epic.key);
    
    setAllowedEpics(
      [
        ...staticEpics,
        ...filteredEpics
      ]
    );
  }, [allowedFeatures, taskList, taskCount]);
  useEffect(() => {
    //store the feature access permissions in local storage
    localStorage.setItem('roleBasedAccessPermissions', JSON.stringify(roleBasedAccessPermissions));
  }, [roleBasedAccessPermissions])
  
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <Box className="w-[90vw] my-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" style={{ alignContent: 'baseline' }}>
        {
          // Filter the mainSidebarItems to remove the first item
          // and then map over the items to create the cards
          mainSidebarItems
            .filter((item) => (item.id !== 0 && allowedEpics.includes(item.key)))
            .map((item) => {
              item.submenu = item.submenu?.filter((submenuItem) => {
                return allowedFeatures.includes(submenuItem.id.toString())
              })
              return item;
            }).map((item) => {
              const images = darkMode ? require.context(
                "@/assets/images/ideation-phase-images/dark-theme",
                true
              )
                : require.context(
                  "@/assets/images/ideation-phase-images",
                  true
                );
              const imagePath = images(`./${item.image}`);
              const isExpanded = cardExpanded === item.id;
              const displayTitle =
                item.key === READY_FOR_ORDERS_FEATURE_KEY
                  ? readyForOrdersLabel.title
                  : item.key === VENDOR_MANAGEMENT_FEATURE_KEY
                    ? vendorManagementLabel.title
                    : item.title;
                            // Calculate the sum of counts
              // const totalTaskCount = (item.key === 'myTasks' && taskCount > 0) ? taskCount : undefined;
              const totalTaskCount = item.key === 'myTasks' ? taskCount : undefined;
              // Helper function to convert to kebab case
              const toKebabCase = (text: string) => {
                if (typeof text !== "string") return "";
                return text.trim().toLowerCase().replace(/\s+/g, "-");
              };

              //Determine the path for page redirection
              let path = '';

              // First condition: check if item.path is present and not "/"
              if (item.path && item.path !== '/') {
                if(item.key === 'myTasks' && taskCount === 0) {
                  path = `/list-fallback?pageTitle=${encodeURIComponent(item?.title)}`;
                } else if (item.key === 'myTasks' && taskList && taskList.length > 0) {
                  // Priority order for dashboard tile click:
                  // 1. "Vendor assessment" (if it exists in the task list)
                  // 2. First group in the list (if "Vendor assessment" doesn't exist)
                  const vendorAssessmentKebab = toKebabCase("Vendor assessment");
                  const vendorAssessmentGroup = taskList.find(
                    (group) => toKebabCase(group.contentTitle) === vendorAssessmentKebab
                  );
                  const defaultTab = vendorAssessmentGroup 
                    ? vendorAssessmentKebab 
                    : toKebabCase(taskList[0]?.contentTitle ?? "");
                  path = `${item.path}?tab=${encodeURIComponent(defaultTab)}`;
                } else {
                  path = item.path;
                }
              } else {
                // Second condition: check if submenu exists and is non-empty
                if (item.submenu && item.submenu.length > 0) {
                  const firstSubmenuItem = item.submenu[0];
                  if (!firstSubmenuItem) {
                    path = "/";
                  } else {
              
                  // Third condition: check if the first submenu item itself has a submenu
                  if (firstSubmenuItem.submenuPath && firstSubmenuItem.submenuPath  !== "/") {
                    // Fourth condition: check if there's a name to construct the path
                    path = item.pathPrefix + firstSubmenuItem.submenuPath;
                  } else {
                    path = `/submenu-fallback?name=${encodeURIComponent(firstSubmenuItem.name || "")}&menuTitle=${encodeURIComponent(displayTitle)}&menuIcon=${encodeURIComponent(item.image)}`;
                  }
                  }
                } else {
                  path = "/"; // Fallback path if no submenu is available
                }
              }
              
              // Filter the submenu based on the allowed features

              let submenu;
              if (!allowedFeatures || allowedFeatures?.length === 0) {
                submenu = item.submenu || [];
              } else {
                submenu = item.submenu && item.submenu.length > 0 ? item.submenu.filter(menu => allowedFeatures?.includes(menu.id.toString())) : [];

              }

              const displaySubmenu = item.key === READY_FOR_ORDERS_FEATURE_KEY && submenu?.length
                ? submenu.map((s) => ({ ...s, name: readyForOrdersLabel.submenuName }))
                : submenu;

              const cardContent = (
                <Suspense fallback={<div>Loading...</div>}>
                  {item.key === 'myTasks' && (
                    <DashboardTileContent
                      tasksList={taskList}
                      path={item.path??''}
                    />
                  )}
                  {(!item.content || item.content.length === 0) &&
                    item.submenu && (
                      <DashboardSubmenu
                        mainMenuTitle={displayTitle}
                        submenu={displaySubmenu ?? []}
                        pathPrefix={item.pathPrefix}
                        icon={item.image}
                        menuId={item.id.toString()}
                      />
                    )}
                </Suspense>
              );
              return (
                <Box key={item.id} className="relative w-full max-w-[800px] dashboard-tile">
                  <Card
                    isDisabled={(allowedEpics?.length === 0) ? true : !(allowedEpics?.includes(item.key))}
                    expanded={isExpanded}
                    title={displayTitle}
                    icon={
                      <DashboardIcon
                        src={imagePath.default.src}
                        alt={displayTitle}
                      />
                    }
                    notificationCount={item.key === 'myTasks' ? totalTaskCount : undefined}
                    content={cardContent}
                    redirectUrl={path}
                    onToggle={() =>{
                      if ((item.key === 'myTasks'  && totalTaskCount === 0) || item.key === 'myNotes') {
                        return; // Do nothing, prevent expansion
                      }
                      setCardExpanded(cardExpanded === item.id ? null : item.id)
                    }}
                    collapseButtonText={buttonText?.VIEW_LESS}
                    expandButtonText={buttonText?.VIEW_MORE}
                    buttonPrimary
                    buttonSize="medium"
                    buttonRounded
                    description={undefined}
                    className={darkMode ? 'dark' : 'light'}
                    buttonDisabled={((item.key === 'myTasks' && totalTaskCount === 0) || item.key === 'myNotes') ? true : false}
                    LinkComponent={Link}
                  />
                </Box>
              );
            })}
      </Box>
    </Suspense>
  );
};

export default DashboardCard;